/**
 * Integrytas Group - Base App (MOBILE)
 * Page: ROR.ROUTING — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/RorRoutingPage.tsx. A DISTINCT
 * presentation (KPI tiles + dense card list + bottom-sheet detail + edit sheet)
 * over the SAME data/logic layer only:
 *   - reads via apiDb (ror_route_rule for the routing rules, ror_freshness for the
 *     read-after-write tracker) — identical tables/columns to the desktop page,
 *   - every write (edit a rule's routing config, toggle it on/off) routes through
 *     the Common Routine via routedUpdate — §2 single write path; never a direct
 *     apiDb write.
 * It never re-renders the desktop grid; §16. ROR config rows carry no _tl tables
 * (internal_code/name are the natural identifiers, tier is a CHECK enum, and
 * entity_name/record_pk/workspace_code are technical routing keys), so there is
 * no FK/locale resolution to do here — the enum label goes through i18n, the
 * freshness timestamp through the locale-aware date formatter. The resolver-test
 * diagnostic and rule creation/deletion stay on the desktop, mirroring how the
 * other mobile screens leave power-user editing to the desktop.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, SquarePen, Route, PenLine } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedUpdate } from '../../lib/routed_writes';
import { useFormatDateTime } from '../../lib/format_date';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'ROR.ROUTING';

type Tier = 'PRIMARY' | 'REPLICA';

interface RuleRow {
  ror_route_rule_id: number;
  internal_code: string;
  name: string | null;
  entity_name: string;
  screen_code: string | null;
  workspace_code: string;
  tier: Tier;
  is_active: 'Y' | 'N';
}

interface FreshRow {
  entity_name: string;
  record_pk: string;
  workspace_code: string;
  written_at: string;
}

type StatusFacet = 'all' | 'active' | 'inactive';
type TierFacet = 'all' | Tier;

// CHECK-enum labels go through i18n via a labelled constant (§17.B11) — never the
// bare stored literal in the JSX.
const TIER_OPTIONS: { value: Tier; labelKey: string; fallback: string }[] = [
  { value: 'REPLICA', labelKey: 'mobile.ror.tier.REPLICA', fallback: 'Replica' },
  { value: 'PRIMARY', labelKey: 'mobile.ror.tier.PRIMARY', fallback: 'Primary' },
];

export function MobileRorRoutingPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [fresh, setFresh] = useState<FreshRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [tierFacet, setTierFacet] = useState<TierFacet>('all');
  const [selected, setSelected] = useState<RuleRow | null>(null);
  const [editing, setEditing] = useState<RuleRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const [r, f] = await Promise.all([
        apiDb
          .from('ror_route_rule')
          .select('ror_route_rule_id, internal_code, name, entity_name, screen_code, workspace_code, tier, is_active')
          .order('internal_code'),
        apiDb
          .from('ror_freshness')
          .select('entity_name, record_pk, workspace_code, written_at')
          .order('written_at', { ascending: false })
          .limit(50),
      ]);
      if (r.error) throw r.error;
      if (f.error) throw f.error;
      setRules((r.data ?? []) as RuleRow[]);
      setFresh((f.data ?? []) as FreshRow[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const tierLabel = (tier: Tier): string => {
    const opt = TIER_OPTIONS.find((o) => o.value === tier);
    return opt ? t(opt.labelKey, opt.fallback) : tier;
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rules.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (tierFacet !== 'all' && r.tier !== tierFacet) return false;
      if (!q) return true;
      return (
        r.internal_code.toLowerCase().includes(q) ||
        (r.name ?? '').toLowerCase().includes(q) ||
        r.entity_name.toLowerCase().includes(q) ||
        (r.screen_code ?? '').toLowerCase().includes(q) ||
        r.workspace_code.toLowerCase().includes(q)
      );
    });
  }, [rules, filter, statusFacet, tierFacet]);

  const kpi = useMemo(() => {
    const total = rules.length;
    const active = rules.filter((r) => r.is_active === 'Y').length;
    const replica = rules.filter((r) => r.is_active === 'Y' && r.tier === 'REPLICA').length;
    return { total, active, replica, writes: fresh.length };
  }, [rules, fresh]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.ror.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const tierFacets: { key: TierFacet; label: string }[] = [
    { key: 'all', label: t('mobile.ror.tier_all', 'Any tier') },
    ...TIER_OPTIONS.map((o) => ({ key: o.value as TierFacet, label: t(o.labelKey, o.fallback) })),
  ];

  // Compact one-line summary of where a rule sends matching reads.
  const targetText = (r: RuleRow): string => {
    const entity = r.entity_name === '*' ? t('mobile.ror.any_entity', 'Any entity') : r.entity_name;
    const parts = [entity];
    if (r.screen_code) parts.push(r.screen_code);
    if (r.workspace_code) parts.push(r.workspace_code);
    return parts.join(' · ');
  };

  const toggleActive = async (r: RuleRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'ror_route_rule', 'ror_route_rule_id', r.ror_route_rule_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.ror.title', 'Read Routing')}
        description={t('mobile.ror.description', 'Rules that route matching reads to a replica, and recent writes pinned to primary.')}
      />

      <MobileStatStrip
        stats={[
          { value: kpi.total, label: t('mobile.ror.stat.rules', 'Rules') },
          { value: kpi.active, label: t('common.boolean.active', 'Active') },
          { value: kpi.replica, label: t('mobile.ror.stat.replica', 'Replica') },
          { value: kpi.writes, label: t('mobile.ror.stat.writes', 'Writes') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.ror.search', 'Search rules')}
          aria-label={t('mobile.ror.search', 'Search rules')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      <MobileFilterChips
        chips={tierFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => tierFacet === v}
        onSelect={(v) => setTierFacet(v as TierFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard>
          <div className="m-empty">
            <Inbox /> {rules.length === 0
              ? t('mobile.ror.empty', 'No route rules — all reads serve from primary')
              : t('mobile.ror.none', 'No rules match')}
          </div>
        </MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.ror_route_rule_id}
              lead={<Route />}
              leadTone={r.tier === 'REPLICA' ? 'blue' : 'slate'}
              title={r.name || r.internal_code}
              subtitle={`${tierLabel(r.tier)} · ${targetText(r)}`}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Recent writes — read-after-write freshness tracker (read-only). */}
      {fresh.length > 0 && (
        <>
          <div className="m-greet">
            <div className="m-sub">{t('mobile.ror.recent_writes', 'Recent writes route to primary within the freshness window')}</div>
          </div>
          <MobileContentCard list>
            {fresh.map((w) => (
              <MobileCardRow
                key={`${w.entity_name}:${w.record_pk}:${w.workspace_code}`}
                lead={<PenLine />}
                leadTone="amber"
                /* fk-display-ok: ror_freshness rows key on the technical entity/table name + record pk, no named entity to resolve */
                title={w.entity_name}
                subtitle={`${t('mobile.ror.record', 'Record')} ${w.record_pk}${w.workspace_code ? ' · ' + w.workspace_code : ''} · ${formatDateTime(w.written_at)}`}
              />
            ))}
          </MobileContentCard>
        </>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.ror.detail', 'Route rule detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Route size={20} /></span>
              <span>
                <div className="m-nm">{selected.name || selected.internal_code}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.ror.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.ror.tier_lbl', 'Tier')} value={tierLabel(selected.tier)} />
              <MobileDetailRow label={t('mobile.ror.entity', 'Entity')} value={selected.entity_name === '*' ? t('mobile.ror.any_entity', 'Any entity') : selected.entity_name} />
              <MobileDetailRow label={t('mobile.ror.screen', 'Screen')} value={selected.screen_code || t('mobile.ror.any', 'Any')} />
              <MobileDetailRow label={t('mobile.ror.workspace', 'Workspace')} value={selected.workspace_code || t('mobile.ror.all_workspaces', 'All')} />
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.ror.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileRorEditSheet
        session={session}
        rule={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — the rule's routing config (target entity/screen/workspace, tier,
// active), saved through the Common Routine (routedUpdate) — §2. internal_code is
// the rule's natural identifier and rule creation/deletion stays on the desktop,
// so the code is shown read-only here (mirrors MobileOrgEditSheet's read-only name).
// ---------------------------------------------------------------------------

interface MobileRorEditSheetProps {
  session: AuthSession;
  rule: RuleRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function MobileRorEditSheet({ session, rule, onClose, onSaved }: MobileRorEditSheetProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [entity, setEntity] = useState('');
  const [screen, setScreen] = useState('');
  const [workspace, setWorkspace] = useState('');
  const [tier, setTier] = useState<Tier>('REPLICA');
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different rule is opened.
  useEffect(() => {
    if (!rule) return;
    setName(rule.name ?? '');
    setEntity(rule.entity_name);
    setScreen(rule.screen_code ?? '');
    setWorkspace(rule.workspace_code);
    setTier(rule.tier);
    setActive(rule.is_active === 'Y');
    setError(null);
  }, [rule]);

  const save = async () => {
    if (!rule) return;
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks). Same
    // normalisation as the desktop saveRule (entity defaults to '*', empty
    // screen/workspace become null/blank).
    const { error: e } = await routedUpdate(session, 'ror_route_rule', 'ror_route_rule_id', rule.ror_route_rule_id, {
      name: name.trim() || null,
      entity_name: entity.trim() || '*',
      screen_code: screen.trim() || null,
      workspace_code: workspace.trim(),
      tier,
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = rule !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.ror.edit_sheet', 'Edit route rule')}>
        <div className="m-grabber" />
        {rule && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Route size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.ror.edit_title', 'Edit route rule')}</div>
                <div className="m-em">{rule.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-ror-name">{t('mobile.ror.name', 'Name')}</label>
                <input
                  id="m-ror-name"
                  className="m-inp"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('mobile.ror.name_placeholder', 'Optional label')}
                  autoFocus
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-ror-entity">{t('mobile.ror.entity', 'Entity')}</label>
                <input
                  id="m-ror-entity"
                  className="m-inp"
                  value={entity}
                  onChange={(e) => setEntity(e.target.value)}
                  placeholder={t('mobile.ror.entity_placeholder', '* = any entity')}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-ror-screen">{t('mobile.ror.screen', 'Screen')}</label>
                <input
                  id="m-ror-screen"
                  className="m-inp"
                  value={screen}
                  onChange={(e) => setScreen(e.target.value)}
                  placeholder={t('mobile.ror.screen_placeholder', 'Any screen')}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-ror-workspace">{t('mobile.ror.workspace', 'Workspace')}</label>
                <input
                  id="m-ror-workspace"
                  className="m-inp"
                  value={workspace}
                  onChange={(e) => setWorkspace(e.target.value)}
                  placeholder={t('mobile.ror.workspace_placeholder', 'All workspaces')}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-ror-tier">{t('mobile.ror.tier_lbl', 'Tier')}</label>
                <select id="m-ror-tier" className="m-sel" value={tier} onChange={(e) => setTier(e.target.value as Tier)}>
                  {TIER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.ror.active_hint', 'Rule routes matching reads when on')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
